<template>
  <div
    :id="addon.state.genericAdvancedSettings?.state?.advanced?.elementId"
    ref="addonRef"
    v-click-away="removeActive"
    class="grid__addon"
    :class="addonClasses"
    :style="addonStyles"
    @mouseover.stop="setFocus"
    @mouseout.stop="removeFocus"
    @click.right="showContextMenu($event, addon, state.edit?.isActive ?? false)"
    @click="toggleActive($event)"
  >
    <div
      v-if="campaignState.isEditModeActive && sectionType === globalState.editing?.activeTabCategory"
      class="edit-title"
    >
      {{ addon.getAddonTitle() }}
    </div>

    <component is="style" v-if="state.customCss" type="text/css">
      {{ state.customCss.state.code }}
    </component>

    <DeleteElement
      v-if="
        state.edit?.isActive &&
        globalState.editing?.activeModel === addon &&
        sectionType === globalState.editing?.activeTabCategory
      "
      :is-green="true"
      :model="addon"
    />

    <component :is="comp" :model="addon" />
    <div v-if="campaignState.isEditModeActive && !state.addonValid" class="grid__addon-invalid-message">
      <i class="grid__addon-invalid-message-icon" :class="editMessageIconClass"></i>
    </div>
  </div>
</template>

<script lang="ts">
import {
  Component,
  computed,
  CSSProperties,
  defineAsyncComponent,
  defineComponent,
  nextTick,
  onMounted,
  PropType,
  ref,
  watch
} from 'vue';
import { AdvancedPositionType, AdvancedWidthType } from '@/typings/enums/enums';
import useNavigator from '@/hooks/useNavigator';
import DeleteElement from '@/components/components/editing/DeleteElement.vue';
import { ContentAddonType } from '@/components/layout/column/ColumnModel';
import { useStore } from '@/store';
import ButtonAddon from '@/components/addons/lf-button/View.vue';
import TextAddon from '@/components/addons/lf-text/View.vue';
import GamePlayAddon from '@/components/addons/gameplay/View.vue';
import GameFlowAddon from '@/components/addons/gameflow/View.vue';
import ImageAddon from '@/components/addons/lf-image/View.vue';
import HeadlineAddon from '@/components/addons/lf-headline/View.vue';
import RegistrationAddon from '@/components/addons/registration/View.vue';
import { ClassList } from '@/typings/types/types';
import { isSsr } from '@/utilities/Utilities';
import { warmAudio } from '@/utilities/AudioPlayer';

const addonComponent: { [key: string]: (() => Promise<Component>) | string } = {
  leaderboard: () => import('@/components/addons/leaderboard/View.vue'),
  addwish: () => import('@/components/addons/addwish/View.vue'),
  countdown: () => import('@/components/addons/countdown/View.vue'),
  'iframe-element': () => import('@/components/addons/iframe-element/View.vue'),
  'iframe-games': () => import('@/components/addons/iframe-games/View.vue'),
  share: () => import('@/components/addons/share/View.vue'),
  rss: () => import('@/components/addons/rss/View.vue'),
  accordion: () => import('@/components/addons/accordion/View.vue'),
  'lf-divider': () => import('@/components/addons/lf-divider/View.vue'),
  'lf-headline': 'addon-lf-headline',
  'lf-text': 'addon-lf-text',
  'lf-image': 'addon-lf-image',
  'lf-animated-headline': () => import('@/components/addons/lf-animated-headline/View.vue'),
  'lf-button': 'addon-lf-button',
  'lf-icon': () => import('@/components/addons/lf-icon/View.vue'),
  'html-element': () => import('@/components/addons/html-element/View.vue'),
  'contact-cta': () => import('@/components/addons/contact-cta/View.vue'),
  googlemap: () => import('@/components/addons/googlemap/View.vue'),
  'lf-share': () => import('@/components/addons/lf-share/View.vue'),
  menu: () => import('@/components/addons/menu/View.vue'),
  prizes: () => import('@/components/addons/prizes/View.vue'),
  registration: 'addon-registration',
  slider: () => import('@/components/addons/slider/View.vue'),
  slideshow: () => import('@/components/addons/slideshow/View.vue'),
  sponsors: () => import('@/components/addons/sponsors/View.vue'),
  tipafriend: () => import('@/components/addons/tipafriend/View.vue'),
  trustpilot: () => import('@/components/addons/trustpilot/View.vue'),
  'video-embed': () => import('@/components/addons/video-embed/View.vue'),
  stepguide: () => import('@/components/addons/stepguide/View.vue'),
  gigya: () => import('@/components/addons/gigya/View.vue'),
  'question-validation': () => import('@/components/addons/question-validation/View.vue'),
  authentication: () => import('@/components/addons/authentication/View.vue'),
  'age-gate': () => import('@/components/addons/age-gate/View.vue'),
  elearning: () => import('@/components/addons/elearning/View.vue'),
  gameflow: 'addon-gameflow',
  gameplay: 'addon-gameplay',
  'social-share': () => import('@/components/addons/social-share/View.vue')
};

interface Props {
  addon: ContentAddonType;
}

export default defineComponent({
  name: 'AddonWrap',
  components: {
    DeleteElement,
    'addon-lf-headline': HeadlineAddon,
    'addon-lf-text': TextAddon,
    'addon-lf-image': ImageAddon,
    'addon-lf-button': ButtonAddon,
    'addon-gameflow': GameFlowAddon,
    'addon-gameplay': GamePlayAddon,
    'addon-registration': RegistrationAddon
  },
  inheritAttrs: false,
  props: {
    addon: {
      type: Object as PropType<ContentAddonType>,
      required: true
    }
  },
  setup(props: Props) {
    const sectionType = props.addon.getSection().getSectionType();
    const store = useStore();
    const addonRef = ref<HTMLElement | null>(null);
    const { setFocus, removeFocus } = useNavigator(props.addon);
    const campaignState = store.getters['campaign/state'];

    let readyPromiseResolve: (() => void) | undefined;

    const readyPromise = new Promise<void>((resolve) => {
      readyPromiseResolve = resolve;
    });

    const shouldAnimateIn = ref(props.addon.state?.genericAdvancedSettings?.state?.transitions?.enabled);
    const animateIn = ref(false);
    const hasAnimatedIn = ref(false);

    const toggleActive = async (event: Event) => {
      if (campaignState.isEditModeActive && sectionType === store.state.editing?.activeTabCategory) {
        warmAudio();
        event.stopPropagation();

        store.dispatch('editing/setActiveModel', {
          model: props.addon
        });

        await nextTick();

        props.addon.column.setHasActiveAddon(true);
      }
    };

    const showContextMenu = (event: MouseEvent, model: ContentAddonType, isActive: boolean) => {
      if (campaignState.isEditModeActive) {
        event.preventDefault();
        event.stopPropagation();
        store.dispatch('editing/showContextMenu', {
          model,
          event,
          isActive
        });
      }
    };

    const removeActive = () => {
      if (campaignState.isEditModeActive && sectionType === store.state.editing?.activeTabCategory) {
        store.dispatch('editing/removeActiveModel');
      }
    };

    const addonClasses = computed(() => {
      const animateClassList: ClassList = {};

      animateClassList[`animated ${props.addon.state.genericAdvancedSettings?.state?.transitions?.type}`] =
        animateIn.value && campaignState.contentReady && !hasAnimatedIn.value;

      const advancedState = props.addon.state.genericAdvancedSettings?.state;

      return {
        [props.addon.state.classIdentifier ? props.addon.state.classIdentifier : '']: true,
        [`grid__addon--${props.addon.state.alias}`]: true,
        [`grid__addon--${props.addon.state.id}`]: true,
        'grid__addon--edit-focus': props.addon.state.edit?.isFocus && !props.addon.state.edit?.isActive,
        'grid__addon--active-in-navigator':
          props.addon.state.edit?.isActive && store.state.editing?.activeModel === props.addon,
        ...(advancedState?.advanced?.className && {
          [`${advancedState?.advanced?.className}`]: true
        }),
        'grid__addon--invalid': !props.addon.state.addonValid && campaignState.isEditModeActive,
        'grid__addon--position-absolute': advancedState?.position?.position === AdvancedPositionType.ABSOLUTE,
        'grid__addon--position-fixed': advancedState?.position?.position === AdvancedPositionType.FIXED,
        ...(advancedState?.generic?.width && {
          'grid__addon--width-full': advancedState?.generic?.width.type === AdvancedWidthType.FULL
        }),
        ...(advancedState?.generic?.width && {
          'grid__addon--width-auto': advancedState?.generic?.width.type === AdvancedWidthType.AUTO
        }),
        ...(advancedState?.generic?.width && {
          'grid__addon--width-custom': advancedState?.generic?.width.type === AdvancedWidthType.CUSTOM
        }),
        ...animateClassList
      };
    });

    const addonStyles = computed<CSSProperties>(() => {
      let addonStyles: CSSProperties = {};
      if (props.addon.state.genericAdvancedSettings?.state?.transitions?.enabled) {
        const enterDuration = props.addon.state.genericAdvancedSettings?.state?.transitions?.duration
          ? props.addon.state.genericAdvancedSettings.state.transitions.duration
          : 525;

        const enterDelay = props.addon.state.genericAdvancedSettings?.state?.transitions?.delay
          ? props.addon.state.genericAdvancedSettings?.state?.transitions.delay
          : 0;

        if (props.addon.state.genericAdvancedSettings?.state?.transitions?.enabled) {
          addonStyles.animationDuration = `${enterDuration}ms`;
          addonStyles.animationDelay = `${enterDelay}ms`;
          addonStyles.transitionDelay = `${enterDelay}ms`;
        }
      }

      if (shouldAnimateIn.value) {
        addonStyles.opacity = 0;
      }

      return {
        ...props.addon.state.genericAdvancedSettings?.state.styling,
        ...addonStyles
      };
    });

    const editMessageIconClass = computed<string[]>(() => {
      if (props.addon.state.alias && campaignState?.edit?.addons) {
        return campaignState.edit.addons[props.addon.state.alias].icon.indexOf('icon-') !== -1
          ? [campaignState.edit.addons[props.addon.state.alias].icon]
          : ['fa', 'fa-' + campaignState.edit.addons[props.addon.state.alias].icon];
      }
      return [];
    });

    let playedEnter = false;

    const onAnimationEnd = () => {
      addonRef.value?.removeEventListener('animationend', onAnimationEnd);
      hasAnimatedIn.value = true;
    };

    onMounted(() => {
      addonRef.value?.addEventListener('animationend', onAnimationEnd);

      if (!isSsr()) {
        // only section should have InterSectionObserver
        if (shouldAnimateIn.value) {
          // observe if section is in intersection
          const observer = new IntersectionObserver(
            ([entry]) => {
              if (entry && entry.isIntersecting && !playedEnter) {
                if (addonRef.value) {
                  if (addonRef.value instanceof HTMLElement) {
                    observer.unobserve(addonRef.value);
                  }
                }
                playedEnter = true;

                // Play animation
                animateIn.value = true;
                shouldAnimateIn.value = false;
              }
            },
            {
              threshold: 0.3
            }
          );

          if (addonRef.value) {
            // @ts-ignore
            observer.observe(addonRef.value);
          }
        }
      }
    });

    watch(
      () => props.addon.state.edit?.isActive,
      (isActive) => {
        if (isActive && campaignState.isEditModeActive && props.addon.column.row.section.state.edit) {
          const element = props.addon.column.row.section.state.edit.sectionRef;
          element?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
        if (!isActive) {
          props.addon.column.setHasActiveAddon(false);
        }
      }
    );

    if (typeof addonComponent[props.addon.state.alias] !== 'function' && readyPromiseResolve) {
      readyPromiseResolve();
    }

    return {
      sectionType,
      addonRef,
      shouldAnimateIn,
      globalState: store.state,
      state: props.addon.state,
      editMessageIconClass,
      showContextMenu,
      toggleActive,
      setFocus,
      removeFocus,
      campaignState,
      addonStyles,
      addonClasses,
      removeActive,
      onBeforeEnter: async () => {
        await readyPromise;

        // Not sure why 2 nextTicks are needed. But they are...
        await new Promise<void>((resolve) => {
          nextTick(resolve);
        });

        await new Promise<void>((resolve) => {
          nextTick(resolve);
        });

        return addonRef.value;
      },
      comp:
        typeof addonComponent[props.addon.state.alias] === 'function'
          ? defineAsyncComponent({
              loader: async () => {
                const asyncLoader = addonComponent[props.addon.state.alias];

                if (typeof asyncLoader !== 'function') {
                  throw new Error(`Unrecognized async addon component "${props.addon.state.alias}"`);
                }

                const component = await asyncLoader();

                if (readyPromiseResolve) {
                  readyPromiseResolve();
                }

                return component;
              }
            })
          : addonComponent[props.addon.state.alias]
    };
  }
});
</script>

<style lang="scss" scoped>
.site--editmode-on {
  .grid__addon {
    position: relative;

    &:before {
      content: '';
      display: block;
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      border: 1px solid transparent;
      z-index: 10;
      pointer-events: none;
    }

    .edit-title {
      display: none;
    }

    &--edit-focus {
      &:before {
        border-style: dashed;
        border-color: $edit-border-hover-color;
      }

      & > .edit-title {
        @include lf-title();
        color: #fff;
        background-color: #878787;
      }
    }

    &--active-in-navigator {
      &:before {
        border-color: $edit-active-border-color;
      }

      & > .edit-title {
        @include lf-title();
        color: #000;
        background-color: $edit-active-border-color;
      }
    }
  }
}
</style>
